import qs from 'qs';
import { SubstitutionRecord } from '@/enterprise/data/useSubstitutionRecords';
import { dateRanges, getRangeKey, type RangeKey } from '@/enterprise/features/history/utils';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';

export type SubstitutionRangeKey = RangeKey | 'allTime';

/**
 * Substitution Records URL parameters. Other pages link here with filters pre-applied through
 * `substitutionRecordsPath`, so renaming any of these breaks those links.
 */
export interface SubstitutionRecordsUrlState {
  /** Inclusive `YYYY-MM-DD` bounds on the substitution date. Absent means all time. */
  startDate: string;
  endDate: string;
  /** Which date option is selected, to tell apart ranges with the same dates (e.g. month to date on day 7). */
  range: string;
  /** Link-only `YYYY-MM-DD` award date. */
  awardDate: string;
}

export interface SubstitutionRecordsFilters {
  startDate?: string;
  endDate?: string;
  rangeKey: SubstitutionRangeKey;
  awardDate?: string;
}

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Builds a link to the Substitution Records page with filters pre-applied.
 */
export const substitutionRecordsPath = (
  params: Partial<Pick<SubstitutionRecordsUrlState, 'startDate' | 'endDate' | 'awardDate'>> = {}
) => {
  const path = asEnterpriseLink('instant-pay/substitution-records');
  const search = qs.stringify(params, { skipNulls: true });

  return search ? `${path}?${search}` : path;
};

const toDateString = (value: unknown) => (typeof value === 'string' && DATE_ONLY_REGEX.test(value) ? value : undefined);

const getRangeKeyFromUrl = (range: unknown, startDate?: string, endDate?: string): SubstitutionRangeKey => {
  if (!startDate || !endDate) {
    return 'allTime';
  }

  if (range === 'custom') {
    return 'custom';
  }

  if (typeof range === 'string' && range in dateRanges) {
    const dates = dateRanges[range as keyof typeof dateRanges];

    if (dates.startDate === startDate && dates.endDate === endDate) {
      return range as keyof typeof dateRanges;
    }
  }

  return getRangeKey(startDate, endDate);
};

/**
 * Reads the URL state into filters, dropping malformed values rather than sending them to SEA.
 */
export const parseSubstitutionRecordsFilters = (
  urlState: Partial<SubstitutionRecordsUrlState>
): SubstitutionRecordsFilters => {
  let startDate = toDateString(urlState.startDate);
  let endDate = toDateString(urlState.endDate);

  // Reason: SEA rejects half-open or inverted ranges with a 400, so treat them as all time.
  if (!startDate || !endDate || startDate > endDate) {
    startDate = undefined;
    endDate = undefined;
  }

  return {
    startDate,
    endDate,
    rangeKey: getRangeKeyFromUrl(urlState.range, startDate, endDate),
    awardDate: toDateString(urlState.awardDate),
  };
};

/**
 * How many filter groups are applied, for the Filters badge. "All time" is the default.
 */
export const countAppliedFilters = (filters: SubstitutionRecordsFilters) =>
  [filters.rangeKey !== 'allTime', !!filters.awardDate].filter(Boolean).length;

/**
 * Applies the award-date filter SEA can't, to records already fetched for the date range.
 */
export const filterSubstitutionRecords = (records: SubstitutionRecord[], { awardDate }: { awardDate?: string }) =>
  records.filter((record) => !awardDate || record.instantPayDate === awardDate);
